import React from "react";
import { MapPin, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { GpsPhotoCapture } from "./GpsPhotoCapture";
import { AuthedImg } from "./GpsPhotoGallery";
import { gpsPhotosToday } from "../lib/gps";

/**
 * Phase 132 — the mandatory "Visit photos" step of the Record Visit drawer (Collector + Supervisor).
 *
 * Counts towards the requirement: GPS photos captured in this drawer (`held`, uploaded when the visit is
 * saved — or queued with it offline) plus GPS photos this user already captured on the property TODAY via the
 * standalone "Add GPS photo" button. Saving is blocked until the total is >= 1.
 */
export function visitPhotoCount(held, property, userId) {
  return (held || []).length + gpsPhotosToday(property?.property_photos, userId).length;
}

export function VisitPhotosStep({ code, property, userId, held, setHeld, testId = "visit-photos" }) {
  const todayMine = gpsPhotosToday(property?.property_photos, userId);
  const total = held.length + todayMine.length;
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5">Visit photos (GPS) *
          <Badge variant={total ? "secondary" : "outline"} className="text-[10px]" data-testid={`${testId}-count`}>{total}</Badge>
        </Label>
        <GpsPhotoCapture testId={`${testId}-capture`} onCapture={(photo) => setHeld((h) => [...h, photo])} />
      </div>
      {(held.length > 0 || todayMine.length > 0) && (
        <div className="flex flex-wrap gap-2" data-testid={`${testId}-thumbs`}>
          {todayMine.map((p) => (
            <div key={p.file_id} className="relative" title="Captured earlier today">
              <AuthedImg code={code} fileId={p.file_id} alt={p.filename || "photo"} className="h-16 w-16 object-cover rounded-md border" />
              <span className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-0.5 text-[9px] py-0.5 bg-emerald-700/85 text-white rounded-b-md">
                <MapPin className="h-2.5 w-2.5" /> GPS
              </span>
            </div>
          ))}
          {held.map((p, i) => (
            <div key={p.client_ref || i} className="relative" data-testid={`${testId}-held-${i}`} title={`GPS ${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`}>
              <img src={p.previewUrl} alt={p.filename || "visit photo"} className="h-16 w-16 object-cover rounded-md border" />
              <span className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-0.5 text-[9px] py-0.5 bg-emerald-700/85 text-white rounded-b-md">
                <MapPin className="h-2.5 w-2.5" /> GPS
              </span>
              <button type="button" onClick={() => setHeld((h) => h.filter((_, j) => j !== i))} title="Remove this photo"
                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`${testId}-remove-${i}`}>
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {total === 0 ? (
        <p className="text-[11px] text-[hsl(var(--warning))] flex items-center gap-1" data-testid={`${testId}-required-hint`}>
          <AlertTriangle className="h-3 w-3 shrink-0" /> At least one GPS-tagged photo of the property is required to save this visit.
        </p>
      ) : (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`${testId}-ok-hint`}>
          <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" />
          {todayMine.length ? `${todayMine.length} GPS photo${todayMine.length === 1 ? "" : "s"} captured earlier today` : ""}
          {todayMine.length && held.length ? " • " : ""}
          {held.length ? `${held.length} new photo${held.length === 1 ? "" : "s"} will be saved with this visit` : ""}
        </p>
      )}
    </div>
  );
}
